import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaTasks, FaChartLine, FaStickyNote, FaPlus } from 'react-icons/fa';
import Layout from '../components/Layout';
import { useNotes } from '../context/NoteContext';
import { useDashboard } from '../context/DashboardContext';
import taskService from '../services/taskService';
import noteServiceInstance from '../services/noteService';
import { useNavigate } from 'react-router-dom';

const handleAddNote = async(navigate) => {
  const noteResponse = await noteServiceInstance.createNote({
    title: `New Note - ${new Date().toLocaleString()}`,
    content: "Date: " + new Date().toLocaleDateString(),
    tags: []
  });
  ////console.log("Note response:", noteResponse);
  const noteId = noteResponse.id;
  navigate('/notes/' + noteId);
}

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: #007bff;
  }
`;

const CardLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  font-size: 0.875rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TaskList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TaskItem = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TaskTitle = styled.span`
  color: ${props => props.isCompleted ? '#6c757d' : 'inherit'};
  text-decoration: ${props => props.isCompleted ? 'line-through' : 'none'};
`;

const TaskDueDate = styled.span`
  font-size: 0.75rem;
  color: ${props => props.isOverdue ? '#dc3545' : '#6c757d'};
`;

const NotesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NoteItem = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const NoteTitle = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const NoteContent = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0069d9;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const StatsItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatsLabel = styled.span`
  color: #555;
`;

const StatsValue = styled.span`
  font-weight: 500;
`;

const ErrorText = styled.p`
  color: #dc3545;
  margin-top: 0.5rem;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
  color: #6c757d;
`;

const HomeScreen = () => {
  const { filteredNotes, fetchNotes, loading: notesLoading, error: notesError } = useNotes();
  const { dashboardData, fetchDashboardData, loading: dashboardLoading, error: dashboardError } = useDashboard();
  const [todayTasks, setTodayTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const navigate = useNavigate();
  // Load data on component mount
  useEffect(() => {
    fetchNotes();
    fetchDashboardData();
    fetchTodayTasks();
  }, []);

  // Fetch today's tasks that are not completed
  const fetchTodayTasks = async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const response = await taskService.getTasks({ taskType: 'Today', page: 1 });
      // Filter only the tasks that are not completed
      const uncompletedTasks = response.tasks.filter(task => !task.is_completed);
      setTodayTasks(uncompletedTasks);
    } catch (error) {
      console.error('Error fetching today tasks:', error);
      setTasksError('Failed to load today\'s tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  // Get recently updated notes
  const recentNotes = filteredNotes
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 3);

  return (
    <Layout>
      <PageTitle>Home</PageTitle>
      
      <CardGrid>
        {/* Tasks Card */}
        <Card>
          <CardHeader>
            <CardTitle><FaTasks />Tasks</CardTitle>
            <CardLink to="/tasks">View All</CardLink>
          </CardHeader>
          
          {tasksLoading ? (
            <LoadingIndicator>Loading tasks...</LoadingIndicator>
          ) : tasksError ? (
            <ErrorText>{tasksError}</ErrorText>
          ) : todayTasks.length > 0 ? (
            <TaskList>
              {todayTasks.map(task => (
                <TaskItem key={task.id}>
                  <TaskTitle isCompleted={task.is_completed}>{task.title}</TaskTitle>
                  <TaskDueDate isOverdue={task.due_date && new Date(task.due_date) < new Date()}>
                    {task.scheduled_date || task.due_date}
                  </TaskDueDate>
                </TaskItem>
              ))}
            </TaskList>
          ) : (
            <p>No pending tasks. Great job!</p>
          )}
          
          <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
            <ActionButton to="/tasks?new=true">
              <FaPlus /> Add New Task
            </ActionButton>
          </div>
        </Card>
        
        {/* Notes Card */}
        <Card>
          <CardHeader>
            <CardTitle><FaStickyNote />Notes</CardTitle>
            <CardLink to="/notes">View All</CardLink>
          </CardHeader>
          
          {notesLoading ? (
            <LoadingIndicator>Loading notes...</LoadingIndicator>
          ) : notesError ? (
            <ErrorText>{notesError}</ErrorText>
          ) : recentNotes.length > 0 ? (
            <NotesList>
              {recentNotes.map(note => (
                <NoteItem key={note.id}>
                  <NoteTitle>{note.title || 'Untitled Note'}</NoteTitle>
                  <NoteContent>{note.summary}</NoteContent>
                </NoteItem>
              ))}
            </NotesList>
          ) : (
            <p>No notes yet. Create your first note!</p>
          )}
          
          <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
            <ActionButton onClick={() => handleAddNote(navigate)}>
              <FaPlus /> Create New Note
            </ActionButton>
          </div>
        </Card>
        
        {/* Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle><FaChartLine />Stats</CardTitle>
            <CardLink to="/dashboard">View Dashboard</CardLink>
          </CardHeader>
          
          {dashboardLoading ? (
            <LoadingIndicator>Loading stats...</LoadingIndicator>
          ) : dashboardError ? (
            <ErrorText>{dashboardError}</ErrorText>
          ) : dashboardData ? (
            <>
              <StatsItem>
                <StatsLabel>Tasks Completed Today:</StatsLabel>
                <StatsValue>{dashboardData.todayStats.completedTasks}</StatsValue>
              </StatsItem>
              <StatsItem>
                <StatsLabel>Pending Tasks Today:</StatsLabel>
                <StatsValue>{dashboardData.todayStats.totalTasks - dashboardData.todayStats.completedTasks}</StatsValue>
              </StatsItem>
              <StatsItem>
                <StatsLabel>Overdue Tasks Today:</StatsLabel>
                <StatsValue>{dashboardData.todayStats.overdueTasks}</StatsValue>
              </StatsItem>
              <StatsItem>
                <StatsLabel>Upcoming Tasks Today:</StatsLabel>
                <StatsValue>{dashboardData.todayStats.upcomingTasks}</StatsValue>
              </StatsItem>
            </>
          ) : (
            <p>No stats available yet.</p>
          )}
        </Card>
      </CardGrid>
    </Layout>
  );
};

export default HomeScreen; 